import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys, useApiMutation, useApiQuery, type ApiClient } from '@zagru/platform';
import type { ReferenceOption, ReferenceSource } from '@zagru/design-system';
import type { QueryOpts } from './common';

/*
 * Governed reference data (D-020, ADR-0040): `/api/v1/platform/refdata`.
 *
 * Every read is tenant-resolved (the tenant's overlay over the system catalogue) and localised by `lang`. Pickers
 * read through `createRefdataSource` — a small stale-while-revalidate cache per tenant and language, so a picker
 * answers from memory while the list refreshes behind it — and the Settings screens through the query hooks below.
 */

export const refdataKindSchema = z.object({
  kind: z.string(),
  label: z.string(),
  description: z.string(),
  tenantCodes: z.boolean(),
  codes: z.number(),
  enabled: z.number(),
});
export type RefdataKind = z.infer<typeof refdataKindSchema>;

export const refdataCodeSchema = z.object({
  kind: z.string(),
  code: z.string(),
  name: z.string(),
  names: z.record(z.string(), z.string()),
  aliases: z.array(z.string()),
  attributes: z.record(z.string(), z.unknown()),
  enabled: z.boolean(),
  favourite: z.boolean(),
  sort: z.number(),
  source: z.enum(['SYSTEM', 'TENANT']),
  overridden: z.boolean(),
  effectiveFrom: z.string().nullable(),
  effectiveTo: z.string().nullable(),
});
export type RefdataCode = z.infer<typeof refdataCodeSchema>;

export const refdataSearchSchema = z.object({ items: z.array(refdataCodeSchema), total: z.number() });
export type RefdataSearch = z.infer<typeof refdataSearchSchema>;

export interface RefdataOverlayCommand {
  enabled?: boolean | null;
  favourite?: boolean | null;
  sort?: number | null;
  names?: Record<string, string> | null;
  aliases?: string[] | null;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
}

export interface RefdataTenantCodeCommand {
  code: string;
  names: Record<string, string>;
  aliases?: string[];
  attributes?: Record<string, unknown>;
  sort?: number | null;
  effectiveFrom?: string | null;
  effectiveTo?: string | null;
}

const base = '/api/v1/platform/refdata';

export const refdataKeys = {
  all: () => queryKeys.custom('platform', 'refdata'),
  kinds: () => queryKeys.custom('platform', 'refdata', 'kinds'),
  search: (kind: string, params: { q?: string; includeDisabled?: boolean; parent?: string; limit?: number; lang?: string }) =>
    queryKeys.custom('platform', 'refdata', kind, 'search', params),
  code: (kind: string, code: string, lang?: string) => queryKeys.custom('platform', 'refdata', kind, 'code', code, lang ?? null),
};

export function useRefdataKinds(options?: QueryOpts<RefdataKind[]>) {
  return useApiQuery<RefdataKind[]>(refdataKeys.kinds(), (client, signal) => client.get(`${base}/kinds`, { schema: z.array(refdataKindSchema), signal }), {
    staleTime: 60_000,
    ...(options ?? {}),
  });
}

export function useRefdataSearch(
  kind: string | undefined,
  params: { q?: string; includeDisabled?: boolean; parent?: string; limit?: number; lang?: string },
  options?: QueryOpts<RefdataSearch>,
) {
  return useApiQuery<RefdataSearch>(
    refdataKeys.search(kind ?? '', params),
    (client, signal) =>
      client.get(`${base}/${encodeURIComponent(kind ?? '')}`, {
        query: { q: params.q || undefined, includeDisabled: params.includeDisabled || undefined, parent: params.parent, limit: params.limit ?? 500, lang: params.lang },
        schema: refdataSearchSchema,
        signal,
      }),
    { enabled: !!kind, staleTime: 30_000, ...(options ?? {}) },
  );
}

export function useRefdataCode(kind: string | undefined, code: string | undefined, lang?: string, options?: QueryOpts<RefdataCode>) {
  return useApiQuery<RefdataCode>(
    refdataKeys.code(kind ?? '', code ?? '', lang),
    (client, signal) => client.get(`${base}/${encodeURIComponent(kind ?? '')}/${encodeURIComponent(code ?? '')}`, { query: { lang }, schema: refdataCodeSchema, signal }),
    { enabled: !!kind && !!code, ...(options ?? {}) },
  );
}

/** After a change the catalogue, every open picker and the Settings lists read again. */
function useRefdataInvalidation() {
  const queryClient = useQueryClient();
  return (kind: string) => {
    invalidateRefdataSources(kind);
    void queryClient.invalidateQueries({ queryKey: refdataKeys.all() });
  };
}

export function usePatchRefdataOverlay() {
  const invalidate = useRefdataInvalidation();
  return useApiMutation<RefdataCode, { kind: string; code: string; body: RefdataOverlayCommand }>(
    (client, { kind, code, body }) => client.patch(`${base}/${encodeURIComponent(kind)}/${encodeURIComponent(code)}/overlay`, body, { schema: refdataCodeSchema }),
    { onSuccess: (_data, vars) => invalidate(vars.kind) },
  );
}

export function usePutRefdataOverlay() {
  const invalidate = useRefdataInvalidation();
  return useApiMutation<RefdataCode, { kind: string; code: string; body: RefdataOverlayCommand }>(
    (client, { kind, code, body }) => client.put(`${base}/${encodeURIComponent(kind)}/${encodeURIComponent(code)}/overlay`, body, { schema: refdataCodeSchema }),
    { onSuccess: (_data, vars) => invalidate(vars.kind) },
  );
}

export function useResetRefdataOverlay() {
  const invalidate = useRefdataInvalidation();
  return useApiMutation<void, { kind: string; code: string }>(
    (client, { kind, code }) => client.delete(`${base}/${encodeURIComponent(kind)}/${encodeURIComponent(code)}/overlay`),
    { onSuccess: (_data, vars) => invalidate(vars.kind) },
  );
}

export function useCreateRefdataCode() {
  const invalidate = useRefdataInvalidation();
  return useApiMutation<RefdataCode, { kind: string; body: RefdataTenantCodeCommand }>(
    (client, { kind, body }) => client.post(`${base}/${encodeURIComponent(kind)}`, body, { schema: refdataCodeSchema, idempotencyKey: true }),
    { onSuccess: (_data, vars) => invalidate(vars.kind) },
  );
}

/* ------------------------------------------------------------------ the pickers' source */

const FRESH_MS = 60_000;
const liveSources = new Set<{ invalidate: (kind: string) => void }>();

/** Forgets every cached answer of `kind` in every live source (after an overlay change). */
export function invalidateRefdataSources(kind: string): void {
  for (const source of liveSources) source.invalidate(kind);
}

const toOption = (c: RefdataCode): ReferenceOption => ({
  kind: c.kind,
  code: c.code,
  name: c.name,
  names: c.names,
  aliases: c.aliases,
  attributes: c.attributes,
  enabled: c.enabled,
  favourite: c.favourite,
});

/**
 * The `ReferenceSource` behind every `ReferencePicker` / `ReferenceDisplay`: stale-while-revalidate per tenant and
 * language. A search answers from its cache when it has one (and refreshes it when older than a minute); every code
 * a search returns is remembered, so a stored value renders by name without a request of its own.
 */

/**
 * A shared lookup runs to completion whoever asked first — its answer is cached for every caller — and each caller
 * only stops waiting when its own signal aborts. (Passing the first caller's signal to the shared request made a
 * re-rendered picker inherit that caller's abort and fall back to the bare code.)
 */
export function awaitUnlessAborted<T>(shared: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return shared;
  if (signal.aborted) return Promise.reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
    signal.addEventListener('abort', onAbort, { once: true });
    shared.then(
      (value) => {
        signal.removeEventListener('abort', onAbort);
        resolve(value);
      },
      (error: unknown) => {
        signal.removeEventListener('abort', onAbort);
        reject(error);
      },
    );
  });
}

export function createRefdataSource(client: ApiClient, scope: { tenantId?: string | null; lang: string }): ReferenceSource & { invalidate: (kind: string) => void; dispose: () => void } {
  const searches = new Map<string, { at: number; items: ReferenceOption[] }>();
  const codes = new Map<string, ReferenceOption | null>();
  const pending = new Map<string, Promise<ReferenceOption | null>>();
  const prefix = `${scope.tenantId ?? '-'}|${scope.lang}|`;
  const codeKey = (kind: string, code: string) => `${prefix}${kind}|${code.toUpperCase()}`;

  const fetchSearch = async (kind: string, query: string, parent: string | undefined, includeDisabled: boolean, limit: number, signal?: AbortSignal) => {
    const page = await client.get(`${base}/${encodeURIComponent(kind)}`, {
      query: { q: query || undefined, parent, includeDisabled: includeDisabled || undefined, limit, lang: scope.lang },
      schema: refdataSearchSchema,
      signal,
    });
    const items = page.items.map(toOption);
    for (const item of items) codes.set(codeKey(kind, item.code), item);
    return items;
  };

  const source = {
    async search(kind: string, query: string, options: { limit?: number; includeDisabled?: boolean; parent?: string; signal?: AbortSignal } = {}) {
      const limit = options.limit ?? 50;
      const key = `${prefix}${kind}|${query.trim().toLowerCase()}|${options.parent ?? ''}|${options.includeDisabled ? 1 : 0}|${limit}`;
      const cached = searches.get(key);
      if (cached) {
        if (Date.now() - cached.at > FRESH_MS) {
          // Stale: answer now, refresh behind.
          void fetchSearch(kind, query, options.parent, !!options.includeDisabled, limit)
            .then((items) => searches.set(key, { at: Date.now(), items }))
            .catch(() => undefined);
        }
        return cached.items;
      }
      const items = await fetchSearch(kind, query, options.parent, !!options.includeDisabled, limit, options.signal);
      searches.set(key, { at: Date.now(), items });
      return items;
    },
    async get(kind: string, code: string, signal?: AbortSignal) {
      const key = codeKey(kind, code);
      if (codes.has(key)) return codes.get(key) ?? null;
      const inFlight = pending.get(key);
      if (inFlight) return awaitUnlessAborted(inFlight, signal);
      const request = client
        .get(`${base}/${encodeURIComponent(kind)}/${encodeURIComponent(code)}`, { query: { lang: scope.lang }, schema: refdataCodeSchema })
        .then((c) => {
          const option = toOption(c);
          codes.set(key, option);
          return option;
        })
        .catch((error: unknown) => {
          if ((error as { status?: number })?.status === 404) {
            codes.set(key, null);
            return null;
          }
          throw error;
        })
        .finally(() => pending.delete(key));
      pending.set(key, request);
      return awaitUnlessAborted(request, signal);
    },
    peek(kind: string, code: string) {
      return codes.get(codeKey(kind, code)) ?? undefined;
    },
    invalidate(kind: string) {
      for (const key of [...searches.keys()]) if (key.startsWith(`${prefix}${kind}|`)) searches.delete(key);
      for (const key of [...codes.keys()]) if (key.startsWith(`${prefix}${kind}|`)) codes.delete(key);
    },
    dispose() {
      liveSources.delete(source);
    },
  };
  liveSources.add(source);
  return source;
}
